import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Menu, Level } from '../../../dbWebUI'
import { Box, Text, Button, Icon } from '@deseretbook/react-ui'
import { roundTo } from '../../../utils/roundTo'

import './ReaderOptions.css'

const fontSizeOptions = ['0.75rem', '1rem', '1.25rem', '1.5rem', '1.75rem']
const fontFamilyOptions = [
  'Georgia',
  'Helvetica',
  'Palatino',
  'Times New Roman',
  'OpenDyslexic',
  'Baskerville',
]

/**
 * Displays a list of reader options. Mainly to be used in a `<Sidebar />` but could be
 * placed in other locations as well.
 * @todo add this to the storybook?
 */
const ReaderOptions = ({
  className,
  onClose,
  readerOptions,
  handleReaderOptionsChange,
  ...rest
}) => {
  const {
    fontFamily,
    fontSize,
    highlightBrightness,
    pageColor,
    spacing,
    alignment,
    layout,
  } = readerOptions

  const [fontSizeIndex, setFontSizeIndex] = useState(fontSizeOptions.indexOf(fontSize))

  const classes = classNames('ReaderOptionsContainer', className)

  return (
    <div id="ReaderOptionsContainer" className={classes} {...rest}>
      <Box
        alignItems="center"
        justifyContent="space-between"
        padding={1}
        style={{ borderBottom: '1px solid #0000001A' }}
      >
        <Box />
        <Text style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '20px' }}>
          Reader Options
        </Text>
        <Button onClick={onClose} className="sidebar-button-close">
          <Icon name="arrowLeft" className="sidebar-button-icon" />
        </Button>
      </Box>
      <Menu hasVerticalScroll style={{ padding: '0 20px' }}>
        <Menu.List>
          <Menu.ListItem>
            <Box
              alignItems="center"
              scrollSnapAlign="end"
              paddingBetweenItems={0.5}
              style={{ padding: '20px 0.75rem 20px 0.75rem', overflowX: 'scroll' }}
            >
              {fontFamilyOptions.map((fontFamilyOption) => {
                const isActive = fontFamily === fontFamilyOption

                return (
                  <Box key={fontFamilyOption} style={{ marginRight: '10px' }}>
                    <Button
                      className={`options-button-font ${isActive ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white' }}
                      onClick={() => handleReaderOptionsChange('fontFamily', fontFamilyOption)}
                    >
                      <Box alignItems="center" justifyContent="center" isColumn>
                        <Text
                          style={{
                            fontFamily: fontFamilyOption,
                            fontSize: '2rem',
                            color: isActive ? 'black' : '#000000A6',
                          }}
                        >
                          Aa
                        </Text>
                        <Text
                          style={{
                            fontFamily: fontFamilyOption,
                            fontSize: '12px',
                            fontWeight: 600,
                            color: isActive ? 'black' : '#000000A6',
                          }}
                        >
                          {fontFamilyOption}
                        </Text>
                      </Box>
                    </Button>
                  </Box>
                )
              })}
            </Box>
          </Menu.ListItem>
          <Menu.ListItem style={{ padding: '20px 0.75rem', borderBottom: '1px solid #0000001A' }}>
            <Level isMobile>
              <Level.Left>
                <Level.Item>
                  <Text className="options-text">Font Size</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Button
                    className="options-button-incremental"
                    onClick={() => {
                      const newValue = Math.max(roundTo(fontSizeIndex - 1), 0)
                      setFontSizeIndex(newValue)
                      handleReaderOptionsChange('fontSize', fontSizeOptions[newValue])
                    }}
                  >
                    <Icon name="minus" />
                  </Button>
                  <Box alignItems="center" isColumn>
                    <Box
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        backgroundColor: 'white',
                        width: '48px',
                        height: '48px',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: fontSizeOptions[fontSizeIndex],
                          padding: '0 0.5rem',
                          fontFamily: 'Roboto, sans-serif',
                        }}
                      >
                        A
                      </Text>
                    </Box>
                    <Box
                      paddingTop={0.25}
                      alignItems="center"
                      justifyContent="space-between"
                      isFullwidth
                    >
                      {['0.75rem', '1rem', '1.25rem', '1.5rem', '1.75rem'].map((value) => (
                        <Box
                          style={{
                            backgroundColor: value === fontSize ? '#1AB698' : '#B6BFBF40',
                            borderRadius: '1rem',
                            height: '6px',
                            width: '6px',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Button
                    className="options-button-incremental"
                    onClick={() => {
                      // decrement font size
                      const newValue = Math.min(roundTo(fontSizeIndex + 1), 4)

                      setFontSizeIndex(newValue)
                      handleReaderOptionsChange('fontSize', fontSizeOptions[newValue])
                    }}
                  >
                    <Icon name="plus" />
                  </Button>
                </Level.Item>
              </Level.Right>
            </Level>
          </Menu.ListItem>
          <Menu.ListItem style={{ padding: '20px 0.75rem', borderBottom: '1px solid #0000001A' }}>
            <Level isMobile>
              <Level.Left>
                <Level.Item>
                  <Text className="options-text">Highlight Brightness</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Button
                    className="options-button-incremental"
                    onClick={() => {
                      const newValue = Math.max(roundTo(highlightBrightness - 0.2), 0.1)

                      handleReaderOptionsChange('highlightBrightness', newValue)
                    }}
                  >
                    <Icon name="minus" />
                  </Button>
                  <Box alignItems="center" isColumn>
                    <Box
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        backgroundColor: `rgba(255, 176, 67, ${highlightBrightness})`,
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '20px',
                          padding: '0 0.5rem',
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 700,
                          color: '#000000A6',
                        }}
                      >
                        A
                      </Text>
                    </Box>
                    <Box
                      paddingTop={0.25}
                      alignItems="center"
                      justifyContent="space-between"
                      isFullwidth
                    >
                      {[0.1, 0.3, 0.5, 0.7, 0.9].map((value) => (
                        <Box
                          style={{
                            backgroundColor:
                              value === highlightBrightness ? '#1AB698' : '#B6BFBF40',
                            borderRadius: '1rem',
                            height: '6px',
                            width: '6px',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Button
                    className="options-button-incremental"
                    onClick={() => {
                      // decrement font size
                      const newValue = Math.min(roundTo(highlightBrightness + 0.2), 0.9)
                      handleReaderOptionsChange('highlightBrightness', newValue)
                    }}
                  >
                    <Icon name="plus" />
                  </Button>
                </Level.Item>
              </Level.Right>
            </Level>
          </Menu.ListItem>
          <Menu.ListItem style={{ padding: '20px 0.75rem', borderBottom: '1px solid #0000001A' }}>
            <Level isMobile>
              <Level.Left>
                <Level.Item>
                  <Text className="options-text">Page Color</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Box alignItems="center">
                    <Button
                      className={`options-button-box ${
                        pageColor && pageColor.id === 'white' ? 'isActive' : ''
                      }`}
                      style={{ backgroundColor: 'white' }}
                      onClick={() =>
                        handleReaderOptionsChange('pageColor', {
                          backgroundColor: 'white',
                          color: 'black',
                          id: 'white',
                        })
                      }
                    >
                      {pageColor && pageColor.id === 'white' && (
                        <Icon className="options-button-box-icon" name="check" />
                      )}
                    </Button>
                    <Button
                      className={`options-button-box ${
                        pageColor && pageColor.id === 'sepia' ? 'isActive' : ''
                      }`}
                      style={{ backgroundColor: '#F5EFDC', margin: '0 16px' }}
                      onClick={() =>
                        handleReaderOptionsChange('pageColor', {
                          backgroundColor: '#F5EFDC',
                          color: '#433E2F',
                          id: 'sepia',
                        })
                      }
                    >
                      {pageColor && pageColor.id === 'sepia' && (
                        <Icon className="options-button-box-icon" name="check" />
                      )}
                    </Button>
                    <Button
                      className={`options-button-box ${
                        pageColor && pageColor.id === 'black' ? 'isActive' : ''
                      }`}
                      style={{ backgroundColor: 'black' }}
                      onClick={() =>
                        handleReaderOptionsChange('pageColor', {
                          backgroundColor: 'black',
                          color: 'white',
                          id: 'black',
                        })
                      }
                    >
                      {pageColor && pageColor.id === 'black' && (
                        <Icon className="options-button-box-icon" name="check" />
                      )}
                    </Button>
                  </Box>
                </Level.Item>
              </Level.Right>
            </Level>
          </Menu.ListItem>
          <Menu.ListItem style={{ padding: '20px 0.75rem' }}>
            <Level isMobile>
              <Level.Left>
                <Level.Item>
                  <Text className="options-text">Spacing</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Box alignItems="center">
                    <Button
                      className={`options-button-box ${spacing === 1.4 ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white' }}
                      onClick={() => handleReaderOptionsChange('spacing', 1.4)}
                    >
                      <Icon className="options-button-box-icon" name="spacingSmall" />
                    </Button>
                    <Button
                      className={`options-button-box ${spacing === 1.6 ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white', margin: '0 16px' }}
                      onClick={() => handleReaderOptionsChange('spacing', 1.6)}
                    >
                      <Icon className="options-button-box-icon" name="spacingMedium" />
                    </Button>
                    <Button
                      className={`options-button-box ${spacing === 1.8 ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white' }}
                      onClick={() => handleReaderOptionsChange('spacing', 1.8)}
                    >
                      <Icon className="options-button-box-icon" name="spacingLarge" />
                    </Button>
                  </Box>
                </Level.Item>
              </Level.Right>
            </Level>
          </Menu.ListItem>
          <Menu.ListItem style={{ padding: '20px 0.75rem', borderBottom: '1px solid #0000001A' }}>
            <Level isMobile>
              <Level.Left>
                <Level.Item>
                  <Text className="options-text">Alignment</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Box alignItems="center">
                    <Button
                      className={`options-button-box ${alignment === 'left' ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white' }}
                      onClick={() => handleReaderOptionsChange('alignment', 'left')}
                    >
                      <Icon className="options-button-box-icon" name="alignLeft" />
                    </Button>
                    <Button
                      className={`options-button-box ${alignment === 'center' ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white', marginLeft: '16px' }}
                      onClick={() => handleReaderOptionsChange('alignment', 'center')}
                    >
                      <Icon className="options-button-box-icon" name="alignCenter" />
                    </Button>
                  </Box>
                </Level.Item>
              </Level.Right>
            </Level>
          </Menu.ListItem>
          <Menu.ListItem style={{ padding: '20px 0.75rem', borderBottom: '1px solid #0000001A' }}>
            <Level isMobile>
              <Level.Left>
                <Level.Item>
                  <Text className="options-text">Layout</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Box alignItems="center">
                    <Button
                      className={`options-button-box ${layout === 1 ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white' }}
                      onClick={() => handleReaderOptionsChange('layout', 1)}
                    >
                      <Icon className="options-button-box-icon" name="singleColumn" />
                    </Button>
                    <Button
                      className={`options-button-box ${layout === 2 ? 'isActive' : ''}`}
                      style={{ backgroundColor: 'white', marginLeft: '16px' }}
                      onClick={() => handleReaderOptionsChange('layout', 2)}
                    >
                      <Icon className="options-button-box-icon" name="doubleColumn" />
                    </Button>
                  </Box>
                </Level.Item>
              </Level.Right>
            </Level>
          </Menu.ListItem>
        </Menu.List>
      </Menu>
    </div>
  )
}

ReaderOptions.defaultProps = {
  tableOfContents: [],
  onSelectChapter: () => {},
  isVisible: true,
  className: '',
}

ReaderOptions.propTypes = {
  /** Table of contents items */
  tableOfContents: PropTypes.arrayOf(PropTypes.object),
  /** callback for selecting a chapter */
  onSelectChapter: PropTypes.func,
  /** if the table of contents is currently being displayed. */
  isVisible: PropTypes.bool,
  /** Additional class names */
  className: PropTypes.string,
}

export default ReaderOptions
